import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Plus, Trash, DownloadSimple, Receipt, CheckCircle, X, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/dateFmt";
import PatientSearchSelect from "@/components/PatientSearchSelect";
import DatePickerZoom from "@/components/DatePickerZoom";

const STATUS_STYLES = {
  draft: "bg-pf-bg text-pf-text-secondary border-pf-border",
  unpaid: "bg-pf-warning-bg text-pf-warning border-pf-warning/30",
  sent: "bg-pf-blue-bg text-pf-blue border-pf-blue/30",
  paid: "bg-pf-success-bg text-pf-success border-pf-success/30",
  void: "bg-pf-danger-bg text-pf-danger border-pf-danger/30",
};

const CURRENCIES = ["USD", "EUR", "GBP", "RON", "INR"];
const SYMBOLS = { USD: "$", EUR: "€", GBP: "£", RON: "lei ", INR: "₹" };

function money(v, c) {
  return `${SYMBOLS[c] || c + " "}${Number(v || 0).toFixed(2)}`;
}

// clear default 0 when user starts typing
function selectOnFocus(e) {
  e.target.select();
}

export default function InvoicesPage() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [patients, setPatients] = useState([]);

  async function load() {
    try {
      const [iRes, pRes] = await Promise.all([
        api.get("/invoices"),
        api.get("/patients", { params: { limit: 1000 } }),
      ]);
      setRows(iRes.data);
      setPatients(pRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function setStatus(id, status) {
    try {
      await api.patch(`/invoices/${id}`, { status });
      load();
      if (viewing?.id === id) {
        const { data } = await api.get(`/invoices/${id}`);
        setViewing(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function del(id) {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      setViewing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function downloadPdf(id) {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const inv = rows.find((r) => r.id === id);
      const filename = `${(inv?.number || "factura").replace(/[^A-Za-z0-9-]/g, "_")}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const statusLabel = (s) => ({
    draft: t("invoiceStatusDraft"),
    unpaid: t("invoiceStatusUnpaid"),
    sent: t("invoiceStatusSent"),
    paid: t("invoiceStatusPaid"),
    void: t("invoiceStatusVoid"),
  }[s] || s);

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-extrabold text-pf-text">{t("invoices")}</h1>
        </div>
        <button data-testid="new-invoice-btn" onClick={() => setOpenNew(true)} className="pf-btn-primary">
          <Plus size={14} weight="bold" /> {t("newInvoice")}
        </button>
      </div>

      {/* Mobile: card list. Desktop (md+): traditional table. */}
      <div className="pf-card md:overflow-x-auto">
        {/* Cards for mobile */}
        <ul className="md:hidden divide-y divide-pf-border">
          {rows.length === 0 && (
            <li className="text-center text-pf-text-secondary py-10">{t("noInvoices")}</li>
          )}
          {rows.map((r) => (
            <li
              key={r.id}
              className="p-4 cursor-pointer hover:bg-pf-bg"
              data-testid={`invoice-row-${r.id}`}
              onClick={() => setViewing(r)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-[11px] text-pf-text-secondary">{r.number}</div>
                  <div className="font-semibold text-pf-text truncate">{r.patient_name || "—"}</div>
                  {r.owner_name && <div className="text-xs text-pf-text-secondary truncate">{r.owner_name}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold">{money(r.total, r.currency)}</div>
                  <span className={`inline-block mt-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${STATUS_STYLES[r.status] || ""}`}>
                    {statusLabel(r.status)}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setViewing(r)} className="pf-btn-ghost text-xs !py-1 !px-2" data-testid={`invoice-view-${r.id}`}>
                  <Eye size={13} /> {t("viewInvoice")}
                </button>
                <button onClick={() => downloadPdf(r.id)} className="pf-btn-ghost text-xs !py-1 !px-2">
                  <DownloadSimple size={13} /> PDF
                </button>
                {r.status !== "paid" && (
                  <button onClick={() => setStatus(r.id, "paid")} className="pf-btn-ghost text-xs !py-1 !px-2 text-pf-success">
                    <CheckCircle size={13} /> {t("markPaid")}
                  </button>
                )}
                <button onClick={() => del(r.id)} className="pf-btn-ghost text-xs !py-1 !px-2 text-pf-danger">
                  <Trash size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
        {/* Traditional table for md+ */}
        <table className="hidden md:table w-full text-sm">
          <thead className="bg-pf-bg text-pf-text-secondary">
            <tr>
              <th className="text-left font-semibold px-4 py-3">{t("invoiceNumber")}</th>
              <th className="text-left font-semibold px-4 py-3">{t("patient")}</th>
              <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t("owner")}</th>
              <th className="text-left font-semibold px-4 py-3">{t("invoiceTotal")}</th>
              <th className="text-left font-semibold px-4 py-3">{t("invoiceStatus")}</th>
              <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t("invoiceIssued")}</th>
              <th className="w-40"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center text-pf-text-secondary py-10">
                  {t("noInvoices")}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-pf-border hover:bg-pf-bg cursor-pointer transition-colors"
                data-testid={`invoice-row-${r.id}`}
                onClick={() => setViewing(r)}
              >
                <td className="px-4 py-3 font-mono text-xs">{r.number}</td>
                <td className="px-4 py-3">
                  <span className="font-semibold text-pf-text">{r.patient_name || "—"}</span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{r.owner_name || "—"}</td>
                <td className="px-4 py-3 font-semibold">{money(r.total, r.currency)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded border ${STATUS_STYLES[r.status] || ""}`}>
                    {statusLabel(r.status)}
                  </span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-pf-text-secondary text-xs">
                  {formatDate(r.issued_at)}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setViewing(r)} className="pf-btn-ghost !py-1 !px-2" title={t("viewInvoice")} data-testid={`invoice-view-${r.id}`}>
                      <Eye size={14} />
                    </button>
                    <button onClick={() => downloadPdf(r.id)} className="pf-btn-ghost !py-1 !px-2" title="PDF">
                      <DownloadSimple size={14} />
                    </button>
                    {r.status !== "paid" && (
                      <button onClick={() => setStatus(r.id, "paid")} className="pf-btn-ghost !py-1 !px-2 text-pf-success" title={t("markPaid")}>
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {r.status !== "void" && r.status !== "paid" && (
                      <button onClick={() => setStatus(r.id, "void")} className="pf-btn-ghost !py-1 !px-2" title={t("voidInvoice")}>
                        <X size={14} />
                      </button>
                    )}
                    <button onClick={() => del(r.id)} className="pf-btn-ghost !py-1 !px-2 text-pf-danger" title={t("delete")}>
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewInvoiceDialog
        open={openNew}
        onOpenChange={setOpenNew}
        patients={patients}
        defaultCurrency={lang === "ro" ? "RON" : "USD"}
        onCreated={() => {
          setOpenNew(false);
          load();
        }}
      />

      <InvoiceViewDialog
        invoice={viewing}
        onClose={() => setViewing(null)}
        onDownload={downloadPdf}
        onSetStatus={setStatus}
        onDelete={del}
        statusLabel={statusLabel}
      />
    </div>
  );
}

function InvoiceViewDialog({ invoice, onClose, onDownload, onSetStatus, onDelete, statusLabel }) {
  const { t } = useLang();
  if (!invoice) return null;
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] max-h-[92dvh] overflow-y-auto p-4 sm:p-6" data-testid="invoice-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Receipt size={18} /> {invoice.number}
            <span className={`ml-2 text-xs font-semibold uppercase px-2 py-0.5 rounded border ${STATUS_STYLES[invoice.status] || ""}`}>
              {statusLabel(invoice.status)}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="pf-label mb-1">{t("patient")}</div>
              <Link to={`/patients/${invoice.patient_id}`} className="text-pf-primary hover:underline font-semibold">
                {invoice.patient_name}
              </Link>
            </div>
            <div>
              <div className="pf-label mb-1">{t("owner")}</div>
              <div>{invoice.owner_name || "—"}</div>
            </div>
            <div>
              <div className="pf-label mb-1">{t("invoiceIssued")}</div>
              <div>{formatDate(invoice.issued_at)}</div>
            </div>
            <div>
              <div className="pf-label mb-1">{t("dueDate")}</div>
              <div>{invoice.due_date ? formatDate(invoice.due_date) : "—"}</div>
            </div>
          </div>

          <div className="border border-pf-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-pf-bg text-pf-text-secondary text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">{t("description")}</th>
                  <th className="text-right px-3 py-2 w-16">{t("qty")}</th>
                  <th className="text-right px-3 py-2 w-24">{t("unitPrice")}</th>
                  <th className="text-right px-3 py-2 w-16">{t("taxPct")}</th>
                  <th className="text-right px-3 py-2 w-24">{t("lineTotal")}</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((it, i) => {
                  // `unit_price` is stored VAT-INCLUSIVE. The "Valoare" column on ANAF
                  // invoices shows the NET value (ex-VAT); VAT is added separately in
                  // the subtotal/total block below.
                  const gross = Number(it.qty || 0) * Number(it.unit_price || 0);
                  const rate = Number(it.tax_pct || 0) / 100;
                  const netLine = rate > 0 ? gross / (1 + rate) : gross;
                  return (
                    <tr key={i} className="border-t border-pf-border">
                      <td className="px-3 py-2">{it.description || "—"}</td>
                      <td className="px-3 py-2 text-right">{it.qty}</td>
                      <td className="px-3 py-2 text-right">{money(it.unit_price, invoice.currency)}</td>
                      <td className="px-3 py-2 text-right">{it.tax_pct}%</td>
                      <td className="px-3 py-2 text-right font-semibold">{money(netLine, invoice.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end flex-col items-end space-y-1">
            <div>{t("subtotal")}: <span className="font-semibold">{money(invoice.subtotal, invoice.currency)}</span></div>
            <div>{t("tax")}: <span className="font-semibold">{money(invoice.tax, invoice.currency)}</span></div>
            <div className="text-lg font-display font-bold text-pf-primary">
              {t("invoiceTotal")}: {money(invoice.total, invoice.currency)}
            </div>
          </div>

          {invoice.notes && (
            <div className="rounded-lg border border-pf-border p-3 bg-pf-bg text-xs">
              <div className="pf-label mb-1">{t("notes")}</div>
              {invoice.notes}
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-pf-border">
            <button onClick={() => onDownload(invoice.id)} className="pf-btn-ghost">
              <DownloadSimple size={14} /> PDF
            </button>
            {invoice.status !== "paid" && (
              <button onClick={() => onSetStatus(invoice.id, "paid")} className="pf-btn-primary" data-testid="inv-mark-paid">
                <CheckCircle size={14} /> {t("markPaid")}
              </button>
            )}
            {invoice.status !== "void" && invoice.status !== "paid" && (
              <button onClick={() => onSetStatus(invoice.id, "void")} className="pf-btn-ghost">
                <X size={14} /> {t("voidInvoice")}
              </button>
            )}
            <button onClick={() => onDelete(invoice.id)} className="pf-btn-ghost text-pf-danger border-pf-danger/30 hover:bg-pf-danger-bg">
              <Trash size={14} /> {t("delete")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewInvoiceDialog({ open, onOpenChange, patients, defaultCurrency = "USD", onCreated }) {
  const { t } = useLang();
  const [patientId, setPatientId] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [dueDate, setDueDate] = useState("");
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: 0, tax_pct: 19 }]);

  useEffect(() => {
    setCurrency(defaultCurrency);
  }, [defaultCurrency]);

  function upd(i, k, v) {
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  }
  function add() {
    setItems((rows) => [...rows, { description: "", qty: 1, unit_price: 0, tax_pct: 19 }]);
  }
  function remove(i) {
    setItems((rows) => rows.filter((_, idx) => idx !== i));
  }

  // `unit_price` is the VAT-INCLUSIVE selling price. Total stays what the vet typed;
  // subtotal + tax are back-calculated from the gross.
  const total = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const subtotal = items.reduce((s, it) => {
    const gross = Number(it.qty || 0) * Number(it.unit_price || 0);
    const rate = Number(it.tax_pct || 0) / 100;
    return s + (rate > 0 ? gross / (1 + rate) : gross);
  }, 0);
  const tax = total - subtotal;

  async function submit(e) {
    e.preventDefault();
    if (!patientId) {
      toast.error(t("selectPatient") || "Select a patient");
      return;
    }
    try {
      await api.post("/invoices", {
        patient_id: patientId,
        currency,
        due_date: dueDate || null,
        items: items.map((it) => ({
          description: it.description,
          qty: Number(it.qty || 0),
          unit_price: Number(it.unit_price || 0),
          tax_pct: Number(it.tax_pct || 0),
        })),
      });
      toast.success(t("invoiceCreated"));
      onCreated();
      setItems([{ description: "", qty: 1, unit_price: 0, tax_pct: 19 }]);
      setPatientId("");
      setDueDate("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] max-h-[92dvh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Receipt size={18} /> {t("newInvoice")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="pf-label mb-1 block">{t("patient")}</label>
              <PatientSearchSelect
                testId="inv-patient"
                patients={patients}
                value={patientId}
                onChange={setPatientId}
                required
                placeholder={t("searchPlaceholder")}
              />
            </div>
            <div>
              <label className="pf-label mb-1 block">{t("currency")}</label>
              <select className="pf-input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="pf-label mb-1 block">{t("dueDate")}</label>
              <DatePickerZoom
                testId="inv-due-date"
                value={dueDate}
                onChange={setDueDate}
              />
            </div>
          </div>

          <div className="border border-pf-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-pf-bg text-pf-text-secondary text-xs uppercase">
                <tr>
                  <th className="text-left px-2 py-1.5">{t("description")}</th>
                  <th className="text-left px-2 py-1.5 w-24">{t("qty")}</th>
                  <th className="text-left px-2 py-1.5 w-32">{t("unitPrice")}</th>
                  <th className="text-left px-2 py-1.5 w-24">{t("taxPct")}</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-t border-pf-border">
                    <td className="px-2 py-1">
                      <input data-testid={`inv-desc-${i}`} className="pf-input" value={it.description} onChange={(e) => upd(i, "description", e.target.value)} />
                    </td>
                    <td className="px-2 py-1">
                      <input data-testid={`inv-qty-${i}`} className="pf-input" type="number" min="0" step="1"
                        value={it.qty}
                        onFocus={selectOnFocus}
                        onChange={(e) => upd(i, "qty", e.target.value === "" ? "" : Number(e.target.value))} />
                    </td>
                    <td className="px-2 py-1">
                      <input data-testid={`inv-price-${i}`} className="pf-input" type="number" min="0" step="0.01"
                        value={it.unit_price}
                        onFocus={selectOnFocus}
                        onChange={(e) => upd(i, "unit_price", e.target.value === "" ? "" : Number(e.target.value))} />
                    </td>
                    <td className="px-2 py-1">
                      <input data-testid={`inv-tax-${i}`} className="pf-input" type="number" min="0" step="0.5"
                        value={it.tax_pct}
                        onFocus={selectOnFocus}
                        onChange={(e) => upd(i, "tax_pct", e.target.value === "" ? "" : Number(e.target.value))} />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <button type="button" onClick={() => remove(i)} className="p-1 text-pf-text-secondary hover:text-pf-danger">
                        <Trash size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-2 border-t border-pf-border">
              <button type="button" onClick={add} className="inline-flex items-center gap-1 text-sm text-pf-primary hover:underline" data-testid="inv-add-row">
                <Plus size={14} weight="bold" /> {t("addRow")}
              </button>
            </div>
          </div>

          <div className="flex justify-end text-sm space-y-1 flex-col items-end">
            <div>{t("subtotal")}: <span className="font-semibold">{money(subtotal, currency)}</span></div>
            <div>{t("tax")}: <span className="font-semibold">{money(tax, currency)}</span></div>
            <div className="text-lg">{t("invoiceTotal")}: <span data-testid="inv-total" className="font-display font-bold text-pf-primary">{money(total, currency)}</span></div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="pf-btn-ghost">{t("cancel")}</button>
            <button data-testid="inv-submit" type="submit" className="pf-btn-primary">{t("createInvoice")}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
